import { PricesBase, PricesRangeWithTotal } from '../../types';
import { getPricesWithTotal } from '../getPricesWithTotal';

/**
 * Calculates the final price by combining base prices with optional variant (discount) prices
 *
 * @param props - Input parameters
 * @param props.basePrice - Base pricing structure (base, price, obFees)
 * @param props.variantPrice - Optional variant pricing to override base prices
 * @param props.nbEmployee - Number of employees for price calculation
 *
 * @returns A PricesRangeWithTotal object containing the final prices,
 * or undefined if variantPrice or nbEmployee is missing
 *
 * The calculation process:
 * 1. Uses variant prices when available, otherwise uses base prices
 * 2. Ensures all price fields have at least 0 as default value
 * 3. Calculates the total monthly cost based on final prices
 *
 * @example
 * ```ts
 * const prices = calculateVariantPrice({
 *   basePrice: {
 *     base: 100,     // Base monthly fee
 *     price: 50,     // Per employee fee
 *     obFees: 500    // One-time setup fee
 *   },
 *   variantPrice: {
 *     base: 80,      // Discounted base fee
 *     price: 40      // Discounted per employee fee
 *   },
 *   nbEmployee: 5
 * });
 * // Returns: {
 * //   base: 80,
 * //   price: 40,
 * //   obFees: 500,
 * //   total: 280     // 80 + (40 * 5)
 * // }
 * ```
 */
type PriceFields = Partial<Pick<PricesBase, 'base' | 'price' | 'obFees'>>;

type CalculateVariantPriceProps = {
  basePrice: PriceFields;
  variantPrice: PriceFields | null | undefined;
  nbEmployee?: number;
};

type CalculateVariantPriceReturn = PricesRangeWithTotal | undefined;
export const calculateVariantPrice = ({
  basePrice,
  variantPrice,
  nbEmployee,
}: CalculateVariantPriceProps): CalculateVariantPriceReturn => {
  if (!variantPrice || nbEmployee === undefined) return undefined;

  return getPricesWithTotal(
    {
      base: variantPrice.base || basePrice.base || 0,
      price: variantPrice.price || basePrice.price || 0,
      obFees: variantPrice.obFees || basePrice.obFees || 0,
    },
    nbEmployee,
  );
};
